'use client'

import { useEffect, useRef, useState } from 'react'

import { IconChevDown, IconClose } from '@/app/components/atoms/Icons'
import { LoadingOverlay } from '@/app/components/shared/LoadingOverlay'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { usePageLoadingOverlay } from '@/app/context/pageLoading'
import {
  NO_APLICA,
  NOT_SET,
  RECENTLY_VIEWED_MODULE_TYPE,
} from '@/app/lib/analytics/constants'
import {
  mapProductToAnalyticsItemBase,
  withIndex,
} from '@/app/lib/analytics/mapProductToAnalyticsItem'
import type { EcommerceItemBase } from '@/app/lib/analytics/types'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'
import { useDragScroll } from '@/app/lib/hooks/useDragScroll'
import { cx } from '@/app/lib/utils'

import { fetchRecentlyViewedAction } from './actions'
import { useRecentlyViewedHistory } from './hooks/useRecentlyViewedHistory'
import { RecentlyViewedCard } from './RecentlyViewedCard'
import {
  CHEVRON_ICON_SIZE,
  CLEAR_ALL_LABEL,
  CLEAR_SHORT_LABEL,
  CLEARED_ANNOUNCEMENT,
  LOADING_LABEL,
  PANEL_ID,
  REMOVE_ICON_SIZE,
  removedAnnouncement,
  removeLabel,
  REVEAL_HOLD_MS,
  SECTION_LABEL,
} from './constants'
import styles from './RecentlyViewed.module.scss'
import type { RecentlyViewedCardItem, RecentlyViewedProps } from './types'
import { mapPerformanceToCard, performanceToProduct } from '@/app/lib/api/mapProductToCard'

/** `item_list_name`/`module_name` del carril — intrínsecos al módulo. */
const ANALYTICS_LIST_NAME = 'vistos_recientemente'

/**
 * "Vistos recientemente" — franja con carril horizontal de los eventos que el
 * visitante ha visitado, tomados de la cookie `recently_viewed_performances`.
 *
 * Client Component por necesidad: la cookie solo se puede leer en el navegador
 * sin marcar como dinámicas las páginas que alojan el bloque (research §1). Los
 * datos se piden por Server Action, así que el api-client no entra en el bundle
 * de cliente (Constitución, Principio IV).
 *
 * No recibe datos de negocio: la página anfitriona solo la monta. Devuelve
 * `null` mientras no haya resultados confirmados y también cuando la respuesta
 * viene vacía o falla, sin ocupar espacio ni mostrar error (FR-005, FR-006,
 * FR-016).
 *
 * Analítica (Guía de Medición v9): el carril es un módulo de producto más, así
 * que emite `view_item_list` (impresiones agrupadas al entrar cada tarjeta en
 * viewport, threshold 0.5) y `select_item` al clicar una, más un
 * `ui_interaction` de tipo `accordion` al plegar/desplegar. Como el componente
 * lo montan varias páginas, todo lo que depende de la página se resuelve solo:
 * el `page_type` de `item_list_id` sale del contexto y `module_position` entra
 * por la prop `analytics`. En una página aún sin instrumentar no falla ni emite
 * nada — `useOptionalPageAnalytics` devuelve `null`.
 */
export function RecentlyViewed({
  defaultOpen = true,
  className,
  analytics: analyticsProps,
}: RecentlyViewedProps) {
  const { ids, remove, clear } = useRecentlyViewedHistory()
  const [items, setItems] = useState<RecentlyViewedCardItem[] | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const [open, setOpen] = useState(defaultOpen)
  /** `true` mientras está en vuelo la petición del lote: la página muestra el
   * overlay de carga general (a peticion de negocio). */
  const [isLoading, setIsLoading] = useState(false)
  /** `true` durante la microanimación de apertura que sigue a la carga en
   * páginas con `defaultOpen` (ver `REVEAL_HOLD_MS`): mantiene el overlay de
   * carga puesto un instante más para que el visitante nunca vea el carril
   * desplegándose a medias. */
  const [isRevealing, setIsRevealing] = useState(false)
  /** La apertura animada solo debe dispararse una vez, al llegar el lote —
   * no en cada `setItems` posterior (quitar una tarjeta, limpiar). */
  const hasRevealedRef = useRef(false)
  const railRef = useRef<HTMLDivElement>(null)
  // Con ratón el carril no se arrastra de forma nativa (solo el gesto táctil).
  useDragScroll(railRef, items?.length ?? 0)
  /** El lote se pide UNA sola vez por carga de página (FR-004b, SC-011). Sin
   * esto, quitar un evento cambia `ids` y relanzaría la petición: una llamada
   * extra al backend por cada baja, y el overlay de carga parpadeando encima de
   * una interacción que debe ser instantánea. Las bajas ya se reflejan en
   * `items`, que es estado local. */
  const hasFetchedRef = useRef(false)
  /** Posición de la tarjeta quitada, para reubicar el foco tras el re-render. */
  const focusIndexRef = useRef<number | null>(null)

  // ── Analítica ──────────────────────────────────────────────────────────────
  // Los `items[]` de e-commerce se construyen del producto CRUDO del lote, no de
  // la tarjeta ya mapeada (que descarta género/precio numérico/fecha ISO), y se
  // guardan por `id` sin `index`: la posición se fija al emitir, porque quitar
  // una tarjeta del carril recoloca a todas las demás.
  const analytics = useOptionalPageAnalytics()
  const analyticsItemsRef = useRef<Map<string, EcommerceItemBase>>(new Map())
  const { trackImpression, trackSelect } = useProductListTracking(
    {
      // `<page_type>_<module_type>` (`Listado filtrado.csv`): el `page_type` sale
      // del contexto de la página anfitriona, así que el mismo carril queda
      // atribuido a la PLP, a la PDP o a postventa según dónde se renderice.
      itemListId: `${analytics?.context.page_type ?? NOT_SET}_${RECENTLY_VIEWED_MODULE_TYPE}`,
      itemListName: ANALYTICS_LIST_NAME,
      moduleName: ANALYTICS_LIST_NAME,
      moduleType: RECENTLY_VIEWED_MODULE_TYPE,
      modulePosition: analyticsProps?.modulePosition ?? NO_APLICA,
    },
    { trackViewItemList: analytics?.trackViewItemList, trackSelectItem: analytics?.trackSelectItem },
  )

  // Se registra en `PageLoadingProvider` (layout de la web general) en vez de
  // pintar su propio overlay directamente: si esta petición arranca justo
  // cuando termina la redirección de `GeoLocationSync` en la misma página, se
  // perciben como un único spinner continuo en vez de dos parpadeos seguidos.
  // Fuera de un provider (tests aislados) `standalone` es `true` y el
  // componente sigue pintando su propio overlay, igual que siempre.
  const { standalone: standaloneOverlay } = usePageLoadingOverlay(isLoading || isRevealing, LOADING_LABEL)

  useEffect(() => {
    // `null` = cookie aún no leída; `[]` = sin historial ⇒ no se pide nada.
    if (ids === null || ids.length === 0) return
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true

    let cancelled = false
    setIsLoading(true)

    // El orden lo manda la cookie, no la respuesta: se indexa por id y se
    // recorre el historial, descartando los ids sin datos (FR-007, FR-008).
    fetchRecentlyViewedAction(ids)
      .then((performances) => {
        if (cancelled) return
        const byId = new Map(performances.map((item) => [item.id, item]))
        setItems(
          ids.flatMap((id) => {
            const performance = byId.get(id)
            if (!performance) return []
            analyticsItemsRef.current.set(
              id,
              mapProductToAnalyticsItemBase(performanceToProduct(performance)),
            )
            return [mapPerformanceToCard(performance)]
          }),
        )
      })
      .catch(() => {
        // La acción ya registra el error y devuelve []; esto solo cubre un
        // fallo de transporte de la propia acción. Sin bloque, sin ruido.
        if (!cancelled) setItems([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [ids])

  // Microanimación de apertura (solo en páginas con `defaultOpen`, p. ej.
  // agenda/lo-más-vendido/promociones — no en subcategoría, que arranca
  // plegada a propósito): en cuanto el lote llega con eventos, mantiene el
  // overlay de carga un instante más (`REVEAL_HOLD_MS`) mientras `.panel`
  // reproduce su animación de entrada (`panelRevealing`, ver el SCSS), para
  // que el visitante nunca vea el carril desplegándose a medio camino — solo
  // el spinner y luego, directamente, el carril ya abierto.
  useEffect(() => {
    if (isLoading || !items || items.length === 0) return
    if (!defaultOpen || hasRevealedRef.current) return
    hasRevealedRef.current = true

    setIsRevealing(true)
    const timeout = setTimeout(() => setIsRevealing(false), REVEAL_HOLD_MS)
    return () => clearTimeout(timeout)
  }, [isLoading, items, defaultOpen])

  // Al quitar una tarjeta desaparece el elemento que tenía el foco. Se devuelve
  // al botón de quitar que ahora ocupa esa posición (o al anterior, si se quitó
  // la última), para no perder el contexto de navegación por teclado
  // (WCAG 2.1 AA — 2.4.3). Si no queda ninguna, la sección se desmonta y no hay
  // dónde ponerlo: el anuncio en la región `status` cubre ese caso.
  useEffect(() => {
    const index = focusIndexRef.current
    if (index === null || !items || items.length === 0) return

    focusIndexRef.current = null
    const buttons = railRef.current?.querySelectorAll<HTMLButtonElement>(
      `.${styles.remove}`,
    )
    buttons?.[Math.min(index, buttons.length - 1)]?.focus()
  }, [items])

  function handleRemove(item: RecentlyViewedCardItem, index: number) {
    focusIndexRef.current = index
    setAnnouncement(removedAnnouncement(item.title))
    setItems((current) => (current ?? []).filter((card) => card.id !== item.id))
    remove(item.id)
  }

  function handleClear() {
    setAnnouncement(CLEARED_ANNOUNCEMENT)
    setItems([])
    clear()
  }

  function handleToggleOpen() {
    const nextOpen = !open
    // Plegado del carril: patrón `accordion` del diccionario
    // (`components-fingerprint/Entradas + FAQs.csv`).
    analytics?.trackUiInteraction({
      component_type: 'accordion',
      component_action: nextOpen ? 'expand' : 'collapse',
      component_id: PANEL_ID,
      component_label: ANALYTICS_LIST_NAME,
      selected_value: NO_APLICA,
      module_name: ANALYTICS_LIST_NAME,
      module_type: RECENTLY_VIEWED_MODULE_TYPE,
      module_position: analyticsProps?.modulePosition ?? NO_APLICA,
    })
    setOpen(nextOpen)
  }

  /**
   * Ítem de e-commerce de una tarjeta, con su posición actual en el carril.
   * `null` si el lote no dejó base para ese `id` (no debería ocurrir: se
   * escriben en el mismo recorrido) — sin base no se emite el evento, antes que
   * emitirlo incompleto.
   */
  function analyticsItemAt(item: RecentlyViewedCardItem, index: number) {
    const base = analyticsItemsRef.current.get(item.id)
    return base ? withIndex(base, index) : null
  }

  // El overlay se pinta aunque todavía no haya tarjetas: mientras dura la
  // petición no hay `items`, y el early return de abajo devolvería `null`. Es
  // `position: fixed`, así que no ocupa espacio en el flujo (FR-016 intacto).
  // Solo se pinta aquí mismo sin `PageLoadingProvider` (`standaloneOverlay`) —
  // con provider, el overlay ya lo pinta él (ver el hook de arriba).
  if (isLoading) return standaloneOverlay ? <LoadingOverlay label={LOADING_LABEL} /> : null

  if (!items || items.length === 0) return null

  return (
    <>
      {/* Se solapa con el carril ya montado debajo (no lo sustituye): tapa la
          microanimación de apertura mientras dura (ver el efecto de arriba).
          Solo hace falta pintarlo aquí sin `PageLoadingProvider`. */}
      {isRevealing && standaloneOverlay && <LoadingOverlay label={LOADING_LABEL} />}
      <section className={cx(styles.section, className)} aria-label={SECTION_LABEL}>
        <div className={styles.inner}>
          <div className={styles.headerRow}>
            {/* El título es el propio control de plegado: se mantiene en la
                jerarquía de encabezados (FR-029) sin dejar de ser operable, que es
                el patrón de *disclosure* del DS (ver el átomo Accordion). */}
            <h2 className={styles.title}>
              <button
                type="button"
                className={styles.trigger}
                aria-expanded={open}
                aria-controls={PANEL_ID}
                onClick={handleToggleOpen}
              >
                <span
                  className={cx(styles.chevron, open && styles.chevronOpen)}
                  aria-hidden="true"
                >
                  <IconChevDown size={CHEVRON_ICON_SIZE} />
                </span>
                {SECTION_LABEL}
              </button>
            </h2>

            {/* Etiqueta compacta en móvil/tablet y completa en escritorio, en el
                mismo botón: el nombre accesible incluye ambas, y CSS decide cuál
                se ve (paridad con la referencia). */}
            <button type="button" className={styles.clearAll} onClick={handleClear}>
              <span className={styles.clearShort}>{CLEAR_SHORT_LABEL}</span>
              <span className={styles.clearFull}>{CLEAR_ALL_LABEL}</span>
            </button>
          </div>

          {/* Plegado con `hidden` + `inert`: el contenido sale del orden de
              tabulación y del árbol de accesibilidad, no solo de la vista.
              `panelRevealing` reproduce la microanimación de apertura una sola
              vez al llegar el lote (ver el efecto de arriba) — puramente
              decorativa, no cambia `open`/`hidden`/`inert`. */}
          <div
            id={PANEL_ID}
            className={cx(styles.panel, open && styles.panelOpen, isRevealing && styles.panelRevealing)}
            hidden={!open}
            inert={!open}
          >
            <div className={styles.rail} ref={railRef}>
              {items.map((item, index) => (
                <div key={item.id} className={styles.slot}>
                  {/* El botón de quitar y "Borrar todo" NO emiten evento: no hay
                      ficha (CSV) ni valor del enum `click_type` que cubra "dar de
                      baja del historial", y el diccionario prohíbe inventarlos.
                      ⚠️ Pendiente de definir con Analítica. `TrackedProductCard`
                      solo delega clicks sobre `a[href]`, así que este `<button>`
                      no dispara `select_item` por accidente. */}
                  <button
                    type="button"
                    className={styles.remove}
                    aria-label={removeLabel(item.title)}
                    onClick={() => handleRemove(item, index)}
                  >
                    <IconClose size={REMOVE_ICON_SIZE} />
                  </button>
                  <TrackedProductCard
                    index={index}
                    onImpression={() => {
                      const analyticsItem = analyticsItemAt(item, index)
                      if (analyticsItem) trackImpression(item.id, analyticsItem)
                    }}
                    onSelect={() => {
                      const analyticsItem = analyticsItemAt(item, index)
                      if (analyticsItem) trackSelect(analyticsItem)
                    }}
                  >
                    <RecentlyViewedCard {...item} />
                  </TrackedProductCard>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fuera del carril a propósito: debe seguir anunciando aunque el bloque
            se pliegue. */}
        <div role="status" aria-live="polite" className={styles.announcer}>
          {announcement}
        </div>
      </section>
    </>
  )
}
